import { useEffect, useMemo } from 'react';

import { useQuotation } from './useQuotation';

import { usePaymentCondition } from '../usePaymentCondition';

import { useTerm } from '../term/useTerm';

const CREDIT_CODE = 'CREDITO';

/**
 * 💳 Lógica de "Forma de pago" para la pantalla de cotización.
 *
 * Espejo de `usePOSPaymentCondition`, pero escribe sobre el objeto anidado
 * `form.paymentCondition` de la cotización (no sobre la venta).
 */
export const useQuotationPaymentCondition = () => {

  const { form, updateField } = useQuotation();

  const {
    paymentConditionDropdown,
    fetchPaymentConditionDropdown,
  } = usePaymentCondition();

  const {
    termDropdown,
    fetchTermDropdown,
  } = useTerm();

  // =====================================================
  // 🔹 Init
  // =====================================================
  useEffect(() => {
    fetchPaymentConditionDropdown();
    fetchTermDropdown();
  }, []);

  // =====================================================
  // 🔹 Estado derivado
  // =====================================================
  const paymentCondition = form.paymentCondition ?? null;

  const isCredit = useMemo(
    () => paymentCondition?.code === CREDIT_CODE,
    [paymentCondition?.code],
  );

  const termCode = paymentCondition?.termCode;

  // =====================================================
  // 🔹 Helpers
  // =====================================================
  const toDateString = (base: Date): string => base.toISOString().split('T')[0];

  const addDaysToToday = (days: number): string => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + days);
    return toDateString(base);
  };

  const addMonthsToToday = (months: number): string => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    base.setMonth(base.getMonth() + months);
    return toDateString(base);
  };

  const addYearsToToday = (years: number): string => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    base.setFullYear(base.getFullYear() + years);
    return toDateString(base);
  };

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handlePaymentConditionChange = (value: string | number) => {
    const selected = paymentConditionDropdown.find(
      (opt: any) => String(opt.id) === String(value),
    );

    if (!selected) {
      updateField('paymentCondition', null);
      return;
    }

    updateField('paymentCondition', {
      id: selected.id,
      name: selected.name,
      code: selected.code,

      termId: undefined,
      termName: undefined,
      termCode: undefined,

      creditDays: undefined,
      creditMonths: undefined,
      creditYears: undefined,

      creditDueDate: undefined,
    });
  };

  const handleTermChange = (value: string | number) => {
    const selectedTerm = termDropdown.find(
      (opt: any) => String(opt.id) === String(value),
    );

    if (!selectedTerm || !paymentCondition) return;

    updateField('paymentCondition', {
      ...paymentCondition,

      termId: selectedTerm.id,
      termName: selectedTerm.name,
      termCode: selectedTerm.code,

      creditDays: undefined,
      creditMonths: undefined,
      creditYears: undefined,

      creditDueDate: undefined,
    });
  };

  const handleCreditDaysChange = (raw: string) => {
    if (!paymentCondition) return;

    const value = raw.trim();

    if (value === '') {
      updateField('paymentCondition', {
        ...paymentCondition,
        creditDays: undefined,
        creditDueDate: undefined,
      });
      return;
    }

    const days = Number(value);

    updateField('paymentCondition', {
      ...paymentCondition,
      creditDays: Number.isNaN(days) ? undefined : days,
      creditMonths: undefined,
      creditYears: undefined,
      creditDueDate: Number.isNaN(days) ? undefined : addDaysToToday(days),
    });
  };

  const handleCreditMonthsChange = (raw: string) => {
    if (!paymentCondition) return;

    const value = raw.trim();

    if (value === '') {
      updateField('paymentCondition', {
        ...paymentCondition,
        creditMonths: undefined,
        creditDueDate: undefined,
      });
      return;
    }

    const months = Number(value);

    updateField('paymentCondition', {
      ...paymentCondition,
      creditMonths: Number.isNaN(months) ? undefined : months,
      creditDays: undefined,
      creditYears: undefined,
      creditDueDate: Number.isNaN(months) ? undefined : addMonthsToToday(months),
    });
  };

  const handleCreditYearsChange = (raw: string) => {
    if (!paymentCondition) return;

    const value = raw.trim();

    if (value === '') {
      updateField('paymentCondition', {
        ...paymentCondition,
        creditYears: undefined,
        creditDueDate: undefined,
      });
      return;
    }

    const years = Number(value);

    updateField('paymentCondition', {
      ...paymentCondition,
      creditYears: Number.isNaN(years) ? undefined : years,
      creditDays: undefined,
      creditMonths: undefined,
      creditDueDate: Number.isNaN(years) ? undefined : addYearsToToday(years),
    });
  };

  const handleCreditDueDateChange = (date: string) => {
    if (!paymentCondition) return;

    updateField('paymentCondition', {
      ...paymentCondition,
      creditDueDate: date,
    });
  };

  return {
    form,

    paymentCondition,

    paymentConditionDropdown,

    termDropdown,

    isCredit,

    termCode,

    handlePaymentConditionChange,

    handleTermChange,

    handleCreditDaysChange,

    handleCreditMonthsChange,

    handleCreditYearsChange,

    handleCreditDueDateChange,
  };
};

export default useQuotationPaymentCondition;
